import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/Button/Button';
import { TextField } from '@/components/TextField/TextField';
import { DepartmentPicker, useSystemDepartments, useStarterPreview } from '@/features/onboarding';
import { t } from '@/lib/i18n';
import { ApiError, NetworkError } from '@/lib/api';
import { signup } from '../api';
import { StarterPreviewPanel } from './StarterPreviewPanel';
import styles from './SignupWizard.module.css';

const step1Schema = z.object({
  first_name: z.string().trim().min(1, 'First name is required.').max(200),
  last_name: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  email: z.string().email('Please enter a valid email address.').max(254),
  password: z.string().min(5, 'Password must be at least 5 characters.').max(200),
  tenantName: z.string().min(1, 'Company name is required.').max(200),
});

type Step1Values = z.infer<typeof step1Schema>;

export function SignupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [step1Data, setStep1Data] = useState<Step1Values | null>(null);
  const [departmentIds, setDepartmentIds] = useState<string[] | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: deptsData } = useSystemDepartments();
  const departments = deptsData?.items ?? [];
  const deptIds = departmentIds ?? [];

  // Pre-fetch preview so Step 3 shows cached data immediately
  const { data: previewData } = useStarterPreview(deptIds);
  const hasStarters = deptIds.length > 0 && (previewData?.members.length ?? 0) > 0;

  const form = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
    defaultValues: { first_name: '', last_name: '', email: '', password: '', tenantName: '' },
  });

  const mutation = useMutation({
    mutationFn: signup,
    onSuccess: (data) => {
      if (data.verificationRequired === false) {
        void navigate('/verify-email-sent?success=1');
      } else {
        void navigate('/verify-email-sent', { state: { email: step1Data?.email } });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const field = (err.details as { field?: string } | undefined)?.field;
        setStep(1);
        if (field === 'email') {
          form.setError('email', { message: t('An account with this email already exists.') });
        } else if (field === 'tenantName') {
          form.setError('tenantName', { message: t('A workspace with that name already exists.') });
        } else {
          setFormError(err.message);
        }
      } else if (err instanceof NetworkError) {
        setFormError(t("Couldn't reach the server. Check your connection and try again."));
      } else {
        setFormError(t('Something went wrong. Please try again.'));
      }
    },
  });

  function onStep1Submit(values: Step1Values) {
    setStep1Data(values);
    setStep(2);
  }

  function onBuildTeam() {
    setStep(3);
  }

  function onCreateAccount() {
    if (!step1Data) return;
    setFormError(null);
    mutation.mutate({ ...step1Data, department_ids: departmentIds });
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {step === 1 && (
          <div className={styles.body}>
            <div className={styles.brand}>{t('Linqage')}</div>
            <div className={styles.progressBar}>
              <div className={`${styles.progressSegment} ${styles.done}`} />
              <div className={styles.progressSegment} />
              <div className={styles.progressSegment} />
            </div>
            <h1 className={styles.heroTitle}>{t('Create your account')}</h1>
            <p className={styles.heroSub}>{t('Step 1 of 3 — your details')}</p>
            <form onSubmit={(e) => void form.handleSubmit(onStep1Submit)(e)} noValidate>
              <div className={styles.fields}>
                <div className={styles.fieldRow}>
                  <TextField
                    label={t('First name')}
                    autoComplete="given-name"
                    error={form.formState.errors.first_name?.message}
                    {...form.register('first_name')}
                  />
                  <TextField
                    label={t('Last name')}
                    autoComplete="family-name"
                    hint={t('Optional.')}
                    error={form.formState.errors.last_name?.message}
                    {...form.register('last_name')}
                  />
                </div>
                <TextField
                  label={t('Work email')}
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  error={form.formState.errors.email?.message}
                  {...form.register('email')}
                />
                <TextField
                  label={t('Password')}
                  type="password"
                  autoComplete="new-password"
                  hint={t('At least 5 characters.')}
                  error={form.formState.errors.password?.message}
                  {...form.register('password')}
                />
                <TextField
                  label={t('Company name')}
                  autoComplete="organization"
                  hint={t('Your team or company. You can rename it later.')}
                  error={form.formState.errors.tenantName?.message}
                  {...form.register('tenantName')}
                />
              </div>
              {formError ? (
                <div className={styles.formError} role="alert">
                  {formError}
                </div>
              ) : null}
              <div className={styles.actions}>
                <Button type="submit" variant="primary" size="lg">
                  {t('Next →')}
                </Button>
              </div>
            </form>
            <p className={styles.altLink}>
              {t('Already have an account?')} <Link to="/login">{t('Sign in')}</Link>
            </p>
          </div>
        )}

        {step === 2 && (
          <div className={styles.body}>
            <div className={styles.brand}>{t('Linqage')}</div>
            <div className={styles.progressBar}>
              <div className={`${styles.progressSegment} ${styles.done}`} />
              <div className={`${styles.progressSegment} ${styles.done}`} />
              <div className={styles.progressSegment} />
            </div>
            <h1 className={styles.heroTitle}>{t('Pick your focus areas')}</h1>
            <p className={styles.heroSub}>
              {t("Step 2 of 3 — choose up to 3 departments. We'll match your starter team.")}
            </p>
            <DepartmentPicker
              departments={departments}
              value={departmentIds}
              onChange={setDepartmentIds}
            />
            <StarterPreviewPanel departmentIds={deptIds} />
            <p className={styles.caveat}>{t('You can swap and add team members at any time.')}</p>
            <div className={styles.step2Actions}>
              <Button variant="primary" size="lg" onClick={onBuildTeam}>
                {t('Build my team →')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                {t('← Back')}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <>
            <div className={styles.body}>
              <div className={styles.brand}>{t('Linqage')}</div>
              <div className={styles.progressBar}>
                <div className={`${styles.progressSegment} ${styles.done}`} />
                <div className={`${styles.progressSegment} ${styles.done}`} />
                <div className={`${styles.progressSegment} ${styles.done}`} />
              </div>
              {hasStarters ? (
                <>
                  <h1 className={styles.heroTitle}>{t('Your team\nis ready.')}</h1>
                  <p className={styles.heroSub}>
                    {t('Specialists matched to your focus areas are standing by.')}
                  </p>
                  <MemberPillsFromPreview departmentIds={deptIds} />
                </>
              ) : (
                <>
                  <h1 className={styles.heroTitle}>{t("You're\nalmost in.")}</h1>
                  <p className={styles.heroSub}>
                    {t('Browse the team catalog and add your first members once you sign in.')}
                  </p>
                </>
              )}
              <p className={styles.journeyLabel}>{t('Your next three moves')}</p>
              <div className={styles.journeyStep}>
                <span className={styles.journeyNum}>1</span>
                <span className={styles.journeyText}>
                  <strong>{t('Meet your team')}</strong>
                  {t(' — read their background and see what they handle')}
                </span>
              </div>
              <div className={styles.journeyStep}>
                <span className={styles.journeyNum}>2</span>
                <span className={styles.journeyText}>
                  <strong>{t('Start a conversation')}</strong>
                  {t(' — tell them what you need and get to know them')}
                </span>
              </div>
              <div className={styles.journeyStep}>
                <span className={styles.journeyNum}>3</span>
                <span className={styles.journeyText}>
                  <strong>{t('Assign work and review')}</strong>
                  {t(' — they handle it; you review the result')}
                </span>
              </div>
              {formError ? (
                <div className={styles.formError} role="alert">
                  {formError}
                </div>
              ) : null}
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  size="lg"
                  isLoading={mutation.isPending}
                  onClick={onCreateAccount}
                >
                  {mutation.isPending ? t('Setting up your workspace…') : t('Create my account →')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                  {t('← Back')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MemberPillsFromPreview({ departmentIds }: { departmentIds: string[] }) {
  const { data } = useStarterPreview(departmentIds);
  const members = data?.members ?? [];
  if (members.length === 0) return null;
  return (
    <div className={styles.memberRow}>
      {members.map((m) => (
        <div key={m.name} className={styles.memberPill}>
          <div className={styles.memberAvatar}>{m.name.charAt(0).toUpperCase()}</div>
          <div className={styles.memberName}>{m.name}</div>
          {m.department ? <div className={styles.memberDept}>{m.department}</div> : null}
          {m.role ? <div className={styles.memberRole}>{m.role}</div> : null}
        </div>
      ))}
    </div>
  );
}
